import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ComplianceApp } from './compliance';
import { MortgageApp } from './mortgage';
import CarrierAffiliations from '../components/CarrierAffiliations';
import { getToken, setSession, clearSession, setDownloadToken } from '../lib/authSession';
import { 
  MessageSquare, 
  FileText, 
  Users, 
  TrendingUp,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Filter,
  Lock,
  LogOut,
  PenLine,
  Trash2,
  Plus,
  Star,
  Eye,
  EyeOff,
  BookOpen,
  Quote,
  X,
  Save,
  Loader2,
  UserCircle,
  Link as LinkIcon,
  Copy,
  Upload,
  Download,
  Settings,
  FolderOpen,
  File,
  FileSpreadsheet,
  Building,
  UserPlus,
  Edit,
  KeyRound,
  Shield,
  FolderArchive
} from 'lucide-react';

import { SmtpSelfTestPanel } from './admin/SmtpSelfTestPanel';
import { BlogModal } from './admin/BlogModal';
import { TestimonialModal } from './admin/TestimonialModal';
import { AgentModal } from './admin/AgentModal';
import { ReferrerModal } from './admin/ReferrerModal';
import { TeamPhoto } from '../components/TeamPhoto';
import AgentDocumentsTab from '../components/AgentDocumentsTab';
import QuoteAnalyticsPanel from '../components/QuoteAnalyticsPanel';
import AdminClientFoldersTab from '../components/AdminClientFoldersTab';
import AdminDealComplianceTab from '../components/AdminDealComplianceTab';
import AdminCalendarTab from '../components/AdminCalendarTab';
import AdminReportsTab from '../components/AdminReportsTab';
import { TranslationPanel } from '../components/TranslationPanel';
import AdminCorrespondenceTab from '../components/AdminCorrespondenceTab';
import TeamTimeTrackingTab from './admin/tabs/TeamTimeTrackingTab';
import { OverviewTab } from './admin/tabs/OverviewTab';
import { InquiriesTab, QuotesTab, ReferralsTab } from './admin/tabs/SubmissionTabs';
import { BlogTab, TestimonialsTab } from './admin/tabs/ContentTabs';
import { AgentsTab, ReferrersTab } from './admin/tabs/PeopleTabs';
import { CommonFilesTab, SettingsTab } from './admin/tabs/FilesSettingsTabs';
import { DraggableTabs, DragHint } from '../components/DraggableTabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin auth is verified server-side via POST /api/admin/login (backed by
// ADMIN_PASSWORD_HASH + ADMIN_PASSWORD_SALT in backend/.env).

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [agents, setAgents] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [tabOrder, setTabOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Selection states for mass delete
  const [selectedInquiries, setSelectedInquiries] = useState([]);
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [selectedReferrals, setSelectedReferrals] = useState([]);
  
  // Modal states
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [saving, setSaving] = useState(false);

  // Common files states
  const [commonFiles, setCommonFiles] = useState([]);
  const [commonFilesCategory, setCommonFilesCategory] = useState('all');
  const [showUploadCommonModal, setShowUploadCommonModal] = useState(false);
  const [commonFileForm, setCommonFileForm] = useState({ category: 'company', name: '', description: '' });
  const [uploadingCommonFile, setUploadingCommonFile] = useState(false);
  const commonFileInputRef = useRef(null);

  // Settings states
  const [siteSettings, setSiteSettings] = useState({ intranet_url: '' });
  const [savingSettings, setSavingSettings] = useState(false);

  // Referrers states
  const [referrers, setReferrers] = useState([]);
  const [showReferrerModal, setShowReferrerModal] = useState(false);
  const [editingReferrer, setEditingReferrer] = useState(null);
  const [referrerReferrals, setReferrerReferrals] = useState([]);

  // Agent compliance documents (admin viewer)
  const [viewingAgentDocs, setViewingAgentDocs] = useState(null); // { id, name }

  // Restore the admin session token (short-lived JWT issued by /api/admin/login).
  // `password` state holds the TOKEN after login — children send it as X-Admin-Password,
  // which the backend accepts as an admin session token.
  useEffect(() => {
    const savedToken = getToken('admin');
    if (savedToken) {
      setPassword(savedToken);
      setIsAuthenticated(true);
    }
    const onExpired = (e) => { if (e.detail?.role === 'admin') handleLogout(); };
    window.addEventListener('utmost-auth-expired', onExpired);
    return () => window.removeEventListener('utmost-auth-expired', onExpired);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  // Load the admin's saved tab ordering (persisted server-side, cross-device).
  const ADMIN_TAB_PREF_KEY = 'admin_tab_order';
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/preferences/${ADMIN_TAB_PREF_KEY}`);
        if (res.ok) {
          const data = await res.json();
          setTabOrder(Array.isArray(data.tab_order) ? data.tab_order : []);
        }
      } catch (err) {
        console.error('Failed to load tab order:', err);
      }
    })();
  }, [isAuthenticated]);

  const saveTabOrder = async (orderedIds) => {
    setTabOrder(orderedIds);
    try {
      await fetch(`${API_URL}/api/preferences/${ADMIN_TAB_PREF_KEY}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tab_order: orderedIds }),
      });
    } catch (err) {
      console.error('Failed to save tab order:', err);
    }
  };

  const resetTabOrder = () => saveTabOrder([]);

  const applyTabOrder = (tabs, order) => {
    if (!order || !order.length) return tabs;
    const byId = Object.fromEntries(tabs.map((t) => [t.id, t]));
    const ordered = order.map((id) => byId[id]).filter(Boolean);
    const remaining = tabs.filter((t) => !order.includes(t.id));
    return [...ordered, ...remaining];
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        setLoginError(typeof err.detail === 'string' ? err.detail : 'Incorrect password. Please try again.');
        return;
      }
      const data = await r.json();
      setSession('admin', data.token);
      if (data.download_token) setDownloadToken(data.download_token, 1800);
      setPassword(data.token);
      setIsAuthenticated(true);
    } catch (err) {
      setLoginError('Login service unavailable. Please try again shortly.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    clearSession('admin');
    sessionStorage.removeItem('utmost_admin_auth');
    sessionStorage.removeItem('utmost_admin_pwd');
    setPassword('');
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, inquiriesRes, quotesRes, referralsRes, blogRes, testimonialsRes, agentsRes, settingsRes, referrersRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`),
        fetch(`${API_URL}/api/contact`),
        fetch(`${API_URL}/api/quote-requests`),
        fetch(`${API_URL}/api/referrals`),
        fetch(`${API_URL}/api/blog?published_only=false`),
        fetch(`${API_URL}/api/testimonials?published_only=false`),
        fetch(`${API_URL}/api/agents/all`),
        fetch(`${API_URL}/api/settings`),
        fetch(`${API_URL}/api/referrers`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (inquiriesRes.ok) setInquiries(await inquiriesRes.json());
      if (quotesRes.ok) setQuotes(await quotesRes.json());
      if (referralsRes.ok) setReferrals(await referralsRes.json());
      if (blogRes.ok) setBlogPosts(await blogRes.json());
      if (testimonialsRes.ok) setTestimonials(await testimonialsRes.json());
      if (agentsRes.ok) setAgents(await agentsRes.json());
      if (settingsRes.ok) setSiteSettings(await settingsRes.json());
      if (referrersRes.ok) setReferrers(await referrersRes.json());
      
      // Fetch common files
      fetchCommonFiles();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommonFiles = async () => {
    try {
      const url = commonFilesCategory === 'all' 
        ? `${API_URL}/api/common-files`
        : `${API_URL}/api/common-files?category=${commonFilesCategory}`;
      const response = await fetch(url);
      if (response.ok) {
        setCommonFiles(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch common files:', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated && activeTab === 'files') {
      fetchCommonFiles();
    }
  }, [commonFilesCategory, activeTab]);

  const updateStatus = async (type, id, newStatus) => {
    try {
      const endpoint = type === 'inquiries' ? 'inquiries' : type === 'quotes' ? 'quotes' : 'referrals';
      const response = await fetch(`${API_URL}/api/${endpoint}/${id}/status?status=${newStatus}`, {
        method: 'PATCH'
      });
      if (response.ok) {
        fetchAllData();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Mass delete functions
  const handleMassDelete = async (type) => {
    const selected = type === 'inquiries' ? selectedInquiries : type === 'quotes' ? selectedQuotes : selectedReferrals;
    if (selected.length === 0) {
      alert('Please select items to delete');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selected.length} ${type}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/${type}/mass-delete`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(selected)
      });
      if (response.ok) {
        fetchAllData();
        if (type === 'inquiries') setSelectedInquiries([]);
        if (type === 'quotes') setSelectedQuotes([]);
        if (type === 'referrals') setSelectedReferrals([]);
      }
    } catch (error) {
      console.error('Error mass deleting:', error);
    }
  };

  const handleDeleteAll = async (type) => {
    if (!window.confirm(`Are you sure you want to delete ALL ${type}? This cannot be undone!`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/${type}/delete-all`, { method: 'DELETE' });
      if (response.ok) {
        fetchAllData();
        if (type === 'inquiries') setSelectedInquiries([]);
        if (type === 'quotes') setSelectedQuotes([]);
        if (type === 'referrals') setSelectedReferrals([]);
      }
    } catch (error) {
      console.error('Error deleting all:', error);
    }
  };

  const toggleSelection = (type, id) => {
    if (type === 'inquiries') {
      setSelectedInquiries(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    } else if (type === 'quotes') {
      setSelectedQuotes(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    } else {
      setSelectedReferrals(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    }
  };

  const toggleSelectAll = (type, items) => {
    const ids = items.map(item => item.id);
    if (type === 'inquiries') {
      setSelectedInquiries(prev => prev.length === ids.length ? [] : ids);
    } else if (type === 'quotes') {
      setSelectedQuotes(prev => prev.length === ids.length ? [] : ids);
    } else {
      setSelectedReferrals(prev => prev.length === ids.length ? [] : ids);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      contacted: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      converted: 'bg-green-100 text-green-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      quoted: 'bg-purple-100 text-purple-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filterByStatus = (items) => {
    if (statusFilter === 'all') return items;
    return items.filter(item => item.status === statusFilter);
  };

  // Blog CRUD
  const handleSaveBlog = async (blogData) => {
    setSaving(true);
    try {
      const method = editingBlog?.id ? 'PUT' : 'POST';
      const url = editingBlog?.id 
        ? `${API_URL}/api/blog/${editingBlog.id}`
        : `${API_URL}/api/blog`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(blogData)
      });
      
      if (response.ok) {
        setShowBlogModal(false);
        setEditingBlog(null);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error saving blog:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      const response = await fetch(`${API_URL}/api/blog/${id}`, { method: 'DELETE' });
      if (response.ok) fetchAllData();
    } catch (error) {
      console.error('Error deleting blog:', error);
    }
  };

  // Testimonial CRUD
  const handleSaveTestimonial = async (testimonialData) => {
    setSaving(true);
    try {
      const method = editingTestimonial?.id ? 'PUT' : 'POST';
      const url = editingTestimonial?.id 
        ? `${API_URL}/api/testimonials/${editingTestimonial.id}`
        : `${API_URL}/api/testimonials`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(testimonialData)
      });
      
      if (response.ok) {
        setShowTestimonialModal(false);
        setEditingTestimonial(null);
        fetchAllData();
      }
    } catch (error) {
      console.error('Error saving testimonial:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      const response = await fetch(`${API_URL}/api/testimonials/${id}`, { method: 'DELETE' });
      if (response.ok) fetchAllData();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
    }
  };

  // Common Files handlers
  const handleUploadCommonFiles = async (e) => {
    e.preventDefault();
    const fileInput = commonFileInputRef.current;
    if (!fileInput?.files || fileInput.files.length === 0) return;

    setUploadingCommonFile(true);
    const formData = new FormData();
    for (let i = 0; i < fileInput.files.length; i++) {
      formData.append('files', fileInput.files[i]);
    }

    try {
      const response = await fetch(
        `${API_URL}/api/common-files?category=${commonFileForm.category}&name=${encodeURIComponent(commonFileForm.name)}&description=${encodeURIComponent(commonFileForm.description || '')}&uploaded_by=Admin`,
        { method: 'POST', body: formData }
      );
      if (response.ok) {
        fetchCommonFiles();
        setShowUploadCommonModal(false);
        setCommonFileForm({ category: 'company', name: '', description: '' });
      }
    } catch (err) {
      console.error('Failed to upload common files:', err);
    } finally {
      setUploadingCommonFile(false);
    }
  };

  const handleDeleteCommonFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      const response = await fetch(`${API_URL}/api/common-files/${fileId}`, { method: 'DELETE' });
      if (response.ok) {
        fetchCommonFiles();
      }
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  };

  // Settings handlers
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch(
        `${API_URL}/api/settings?intranet_url=${encodeURIComponent(siteSettings.intranet_url || '')}&compliance_officer_email=${encodeURIComponent(siteSettings.compliance_officer_email || '')}&alert_email=${encodeURIComponent(siteSettings.alert_email || '')}`,
        { method: 'PUT' }
      );
      if (response.ok) {
        alert('Settings saved successfully!');
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  // Helper functions for file display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (fileType?.includes('spreadsheet') || fileType?.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
    if (fileType?.includes('word') || fileType?.includes('document')) return <FileText className="h-5 w-5 text-blue-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'company': 'Company',
      'mortgage': 'Mortgage',
      'insurance_investments': 'Insurance & Investments'
    };
    return labels[category] || category;
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003D5C] to-[#0077B6] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-800">Admin Dashboard</CardTitle>
            <p className="text-gray-500 mt-2">Enter your password to continue</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent pr-12"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {loginError && (
                <p className="text-red-500 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {loginError}
                </p>
              )}
              <Button type="submit" className="w-full bg-[#0077B6] hover:bg-[#005A8C]">
                <Lock className="h-4 w-4 mr-2" />
                Access Dashboard
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-[#0077B6] mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const ctx = {
    apiUrl: API_URL, password,
    stats, inquiries, quotes, referrals, blogPosts, testimonials, agents, referrers,
    commonFiles, commonFilesCategory, siteSettings, expandedItems, statusFilter,
    selectedInquiries, selectedQuotes, selectedReferrals, savingSettings,
    setAgents, setReferrers, setActiveTab, setCommonFilesCategory,
    setEditingBlog, setShowBlogModal, setEditingTestimonial, setShowTestimonialModal,
    setEditingAgent, setShowAgentModal, setEditingReferrer, setShowReferrerModal,
    setViewingAgentDocs, setSiteSettings, setShowUploadCommonModal,
    updateStatus, handleMassDelete, handleDeleteAll, toggleSelection, toggleSelectAll,
    toggleExpanded, getStatusBadge, formatDate, filterByStatus,
    handleDeleteBlog, handleDeleteTestimonial, handleDeleteCommonFile, handleSaveSettings,
    formatFileSize, getFileIcon, getCategoryLabel, fetchAllData,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#003D5C] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-blue-200 mt-1">Manage inquiries, quotes, referrals, blog posts & testimonials</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={fetchAllData}
                variant="outline" 
                className="border-white text-white hover:bg-white/10"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button 
                onClick={handleLogout}
                variant="outline" 
                className="border-red-300 text-red-300 hover:bg-red-500/20"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Contact Inquiries</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.inquiries.total}</p>
                    <p className="text-xs text-blue-600">{stats.inquiries.new} new</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Quote Requests</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.quotes.total}</p>
                    <p className="text-xs text-green-600">{stats.quotes.new} new</p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <FileText className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Credit Referrals</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.referrals.total}</p>
                    <p className="text-xs text-purple-600">{stats.referrals.new} new</p>
                  </div>
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Blog Posts</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.blog_posts}</p>
                    <p className="text-xs text-amber-600">Published</p>
                  </div>
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Testimonials</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.testimonials}</p>
                    <p className="text-xs text-orange-600">Active</p>
                  </div>
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Quote className="h-5 w-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-8">
        <div className="flex items-center justify-between mb-2">
          <DragHint />
          {tabOrder.length > 0 && (
            <button
              onClick={resetTabOrder}
              data-testid="reset-tab-order-btn"
              className="text-xs text-gray-400 hover:text-amber-600 underline underline-offset-2"
            >
              Reset order
            </button>
          )}
        </div>
        {(() => {
          const allTabs = [
            { id: 'overview', label: 'Overview', color: 'from-slate-500 to-slate-600' },
            { id: 'inquiries', label: 'Inquiries', color: 'from-blue-500 to-blue-600' },
            { id: 'quotes', label: 'Quotes', color: 'from-emerald-500 to-emerald-600' },
            { id: 'quote-stats', label: 'Quote Stats', color: 'from-fuchsia-500 to-pink-500' },
            { id: 'referrals', label: 'Referrals', color: 'from-violet-500 to-violet-600' },
            { id: 'blog', label: 'Blog', color: 'from-pink-500 to-rose-500' },
            { id: 'testimonials', label: 'Testimonials', color: 'from-amber-500 to-orange-500' },
            { id: 'agents', label: 'Agents', color: 'from-cyan-500 to-teal-500' },
            { id: 'referrers', label: 'Referrers', color: 'from-yellow-500 to-amber-500' },
            { id: 'files', label: 'Common Files', color: 'from-indigo-500 to-purple-500' },
            { id: 'client-folders', label: 'Client Folders', color: 'from-emerald-500 to-teal-600' },
            { id: 'compliance', label: 'Insurance App Workflow', color: 'from-teal-500 to-cyan-500' },
            { id: 'mortgage-workflow', label: 'Mortgage App Workflow', color: 'from-blue-600 to-sky-600' },
            { id: 'deal-compliance', label: 'Deal Compliance Review', color: 'from-rose-600 to-red-600' },
            { id: 'calendar', label: 'Calendar', color: 'from-indigo-500 to-violet-500' },
            { id: 'reports', label: 'Reports', color: 'from-orange-500 to-red-500' },
            { id: 'translation', label: 'Translation', color: 'from-fuchsia-500 to-pink-500' },
            { id: 'correspondence', label: 'Correspondence', color: 'from-blue-600 to-sky-500' },
            { id: 'time-tracking', label: 'Team Time Tracking', color: 'from-green-600 to-emerald-600' },
            { id: 'settings', label: 'Settings', color: 'from-gray-600 to-gray-700' }
          ];
          const orderedTabs = applyTabOrder(allTabs, tabOrder);
          return (
            <DraggableTabs
              tabs={orderedTabs}
              onReorder={saveTabOrder}
              className="flex flex-wrap gap-2 p-2 rounded-xl bg-gray-100/80 backdrop-blur w-fit shadow-inner"
              renderTab={(tab) => (
                <button
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? `bg-gradient-to-r ${tab.color} text-white shadow-md`
                      : 'text-gray-600 hover:bg-white/60 hover:text-gray-800'
                  }`}
                >
                  {tab.label}
                </button>
              )}
            />
          );
        })()}

        {/* Filter for submission tabs */}
        {['inquiries', 'quotes', 'referrals'].includes(activeTab) && (
          <div className="mt-4 flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              {activeTab === 'inquiries' && <option value="resolved">Resolved</option>}
              {activeTab === 'quotes' && <option value="quoted">Quoted</option>}
              {activeTab === 'quotes' && <option value="converted">Converted</option>}
              {activeTab === 'referrals' && <option value="in_progress">In Progress</option>}
              {activeTab === 'referrals' && <option value="completed">Completed</option>}
            </select>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && <OverviewTab ctx={ctx} />}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && <InquiriesTab ctx={ctx} />}

        {/* Quotes Tab */}
        {activeTab === 'quotes' && <QuotesTab ctx={ctx} />}

        {/* Quote Stats Tab (insurance quote funnel analytics) */}
        {activeTab === 'quote-stats' && (
          <QuoteAnalyticsPanel apiUrl={API_URL} adminPassword={password} />
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && <ReferralsTab ctx={ctx} />}

        {/* Blog Posts Tab */}
        {activeTab === 'blog' && <BlogTab ctx={ctx} />}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && <TestimonialsTab ctx={ctx} />}

        {/* Agents Tab */}
        {activeTab === 'agents' && <AgentsTab ctx={ctx} />}

        {/* Referrers Tab */}
        {activeTab === 'referrers' && <ReferrersTab ctx={ctx} />}

        {/* Common Files Tab */}
        {activeTab === 'files' && <CommonFilesTab ctx={ctx} />}

        {/* Compliance Files Tab */}
        {activeTab === 'compliance' && (
          <div className="-mx-4 sm:-mx-6 -mt-6">
            <ComplianceApp agent={{ id: 'admin', name: 'Admin', license_number: 'ADMIN' }} isAdmin={true} />
          </div>
        )}

        {activeTab === 'mortgage-workflow' && (
          <div className="-mx-4 sm:-mx-6 -mt-6 px-4 sm:px-6 pt-2">
            <MortgageApp agent={{ id: 'admin', name: 'Admin' }} isAdmin={true} />
          </div>
        )}

        {/* Client Folders Tab — admin view across every agent */}
        {activeTab === 'client-folders' && (
          <AdminClientFoldersTab apiUrl={API_URL} adminPassword={password} />
        )}

        {activeTab === 'deal-compliance' && (
          <AdminDealComplianceTab apiUrl={API_URL} adminPassword={password} />
        )}

        {/* Calendar Tab — admin broadcasts events to agents by division */}
        {activeTab === 'calendar' && (
          <AdminCalendarTab apiUrl={API_URL} adminPassword={password} />
        )}

        {/* Reports Tab — agent roster, activity, compliance, pipeline, marketing exports */}
        {activeTab === 'reports' && (
          <AdminReportsTab apiUrl={API_URL} adminPassword={password} />
        )}

        {/* Iter 34 — Document Translation tab (admin sees all advisors' translations) */}
        {activeTab === 'translation' && (
          <TranslationPanel mode="admin" apiUrl={API_URL} adminPwd={password} />
        )}

        {/* Iter 45 — Admin Correspondence (memos / business comms, immutable) */}
        {activeTab === 'correspondence' && (
          <AdminCorrespondenceTab apiUrl={API_URL} adminPassword={password} />
        )}

        {activeTab === 'time-tracking' && <TeamTimeTrackingTab />}

        {/* Settings Tab */}
        {activeTab === 'settings' && <SettingsTab ctx={ctx} />}
      </div>

      {/* Blog Modal */}
      {showBlogModal && (
        <BlogModal
          blog={editingBlog}
          onSave={handleSaveBlog}
          onClose={() => {
            setShowBlogModal(false);
            setEditingBlog(null);
          }}
          saving={saving}
        />
      )}

      {/* Testimonial Modal */}
      {showTestimonialModal && (
        <TestimonialModal
          testimonial={editingTestimonial}
          onSave={handleSaveTestimonial}
          onClose={() => {
            setShowTestimonialModal(false);
            setEditingTestimonial(null);
          }}
          saving={saving}
        />
      )}

      {/* Agent Modal */}
      {showAgentModal && (
        <AgentModal
          agent={editingAgent}
          onSave={async (agentData) => {
            setSaving(true);
            try {
              const isEditing = !!editingAgent;
              const url = isEditing 
                ? `${API_URL}/api/agents/${editingAgent.id}`
                : `${API_URL}/api/agents`;
              const method = isEditing ? 'PUT' : 'POST';
              
              const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(agentData)
              });
              
              if (response.ok) {
                const result = await response.json();
                if (isEditing) {
                  setAgents(agents.map(a => a.id === editingAgent.id ? result.agent : a));
                } else {
                  setAgents([...agents, result.agent]);
                }
                setShowAgentModal(false);
                setEditingAgent(null);
              } else {
                const error = await response.json();
                alert(error.detail || 'Error saving agent');
              }
            } catch (err) {
              console.error('Error saving agent:', err);
              alert('Error saving agent');
            } finally {
              setSaving(false);
            }
          }}
          onClose={() => {
            setShowAgentModal(false);
            setEditingAgent(null);
          }}
          saving={saving}
        />
      )}

      {/* Referrer Modal */}
      {showReferrerModal && (
        <ReferrerModal
          referrer={editingReferrer}
          onSave={async (referrerData) => {
            setSaving(true);
            try {
              const isEditing = !!editingReferrer;
              const url = isEditing 
                ? `${API_URL}/api/referrers/${editingReferrer.id}`
                : `${API_URL}/api/referrers`;
              const method = isEditing ? 'PUT' : 'POST';
              
              const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(referrerData)
              });
              
              if (response.ok) {
                const result = await response.json();
                if (isEditing) {
                  setReferrers(referrers.map(r => r.id === editingReferrer.id ? result.referrer : r));
                } else {
                  setReferrers([...referrers, result.referrer]);
                  // Show temp password if email wasn't sent
                  if (result.temp_password) {
                    alert(`Referrer created!\n\nTemporary Password: ${result.temp_password}\n\nPlease share this with the referrer.`);
                  }
                }
                setShowReferrerModal(false);
                setEditingReferrer(null);
                fetchAllData();
              } else {
                const error = await response.json();
                alert(error.detail || 'Error saving referrer');
              }
            } catch (err) {
              console.error('Error saving referrer:', err);
              alert('Error saving referrer');
            } finally {
              setSaving(false);
            }
          }}
          onClose={() => {
            setShowReferrerModal(false);
            setEditingReferrer(null);
          }}
          saving={saving}
        />
      )}

      {/* Upload Common File Modal */}
      {showUploadCommonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Upload Common Files</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowUploadCommonModal(false)}><X className="h-5 w-5" /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUploadCommonFiles} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <select 
                    value={commonFileForm.category} 
                    onChange={(e) => setCommonFileForm({...commonFileForm, category: e.target.value})} 
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                  >
                    <option value="company">Company (All Agents)</option>
                    <option value="mortgage">Mortgage (Mortgage Licensees Only)</option>
                    <option value="insurance_investments">Insurance & Investments (Ins/Inv Licensees Only)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Display Name *</label>
                  <input 
                    type="text" 
                    value={commonFileForm.name} 
                    onChange={(e) => setCommonFileForm({...commonFileForm, name: e.target.value})} 
                    required 
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" 
                    placeholder="e.g., DLC Submission Checklist" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea 
                    value={commonFileForm.description} 
                    onChange={(e) => setCommonFileForm({...commonFileForm, description: e.target.value})} 
                    rows={2} 
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" 
                    placeholder="Brief description of this file..." 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">File(s) *</label>
                  <input ref={commonFileInputRef} type="file" multiple required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowUploadCommonModal(false)} className="flex-1">Cancel</Button>
                  <Button type="submit" disabled={uploadingCommonFile} className="flex-1 bg-[#0077B6]">
                    {uploadingCommonFile ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : 'Upload Files'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admin: View Agent Compliance Documents Modal */}
      {viewingAgentDocs && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="admin-docs-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FolderArchive className="h-5 w-5 text-[#0077B6]" />
                {viewingAgentDocs.name}'s Compliance Documents
              </h2>
              <button
                onClick={() => setViewingAgentDocs(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                data-testid="close-admin-docs-modal"
              >×</button>
            </div>
            <div className="p-5">
              <AgentDocumentsTab
                agentId={viewingAgentDocs.id}
                apiUrl={API_URL}
                adminMode={true}
                adminPassword={password}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Blog Modal Component
export default AdminDashboard;
